import { useState } from "react";
import { Download, Copy, Check, Mail, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PALETTE = [
  { name: "Ember Orange", hex: "#E65F2B", role: "Primary brand · CTAs, accents" },
  { name: "Gold", hex: "#EAB308", role: "Highlights · admin badges" },
  { name: "Cream", hex: "#FDF0CA", role: "Logo background · light surfaces" },
  { name: "Ink Black", hex: "#0A0A0A", role: "Backgrounds · primary surface" },
  { name: "Bone White", hex: "#FFFFFF", role: "Body text on dark" },
  { name: "Smoke", hex: "rgba(255,255,255,0.05)", role: "Card surfaces · 5% white" },
];

const LOGOS = [
  {
    label: "Primary · on light",
    description: "Default lockup. Use on white, cream, or photographic backgrounds.",
    preview: "/press/christoreel-logo-cream-512.png",
    bgClass: "bg-[#FDF0CA]",
    downloads: [
      { size: "512px PNG", href: "/press/christoreel-logo-cream-512.png" },
      { size: "1024px PNG", href: "/press/christoreel-logo-cream-1024.png" },
    ],
  },
  {
    label: "Reverse · on dark",
    description: "For black, ink, and deep-color backgrounds. Cream avatar against ink.",
    preview: "/press/christoreel-logo-dark-512.png",
    bgClass: "bg-[#0a0a0a]",
    downloads: [
      { size: "512px PNG", href: "/press/christoreel-logo-dark-512.png" },
      { size: "1024px PNG", href: "/press/christoreel-logo-dark-1024.png" },
    ],
  },
  {
    label: "Round · transparent",
    description: "Circular crop with transparent corners. Drop onto any surface.",
    preview: "/press/christoreel-logo-round-512.png",
    bgClass: "bg-gradient-to-br from-[#1f1f1f] to-[#0a0a0a]",
    downloads: [
      { size: "512px PNG", href: "/press/christoreel-logo-round-512.png" },
      { size: "1024px PNG", href: "/press/christoreel-logo-round-1024.png" },
    ],
  },
];

const BOILERPLATE = `Christoreel is the streaming home for African Christian cinema — a Naira-first, faith-rooted platform where filmmakers across the continent build their own channels and audiences discover Light-filled stories. Founded to honor the storytellers who carry the Gospel through moving image, Christoreel curates a growing catalog of films from creators in Nigeria, Kenya, Ghana, South Africa and beyond.`;

export default function Press() {
  const [copied, setCopied] = useState(null);

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(null), 1600);
    } catch {
      toast.error("Couldn't copy — select and copy manually.");
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16" data-testid="press-page">
      {/* Hero */}
      <div className="flex items-center gap-3 mb-3">
        <Sparkles className="h-4 w-4 text-[#E65F2B]" />
        <p className="text-eyebrow text-[#E65F2B]">Press &amp; brand kit</p>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
        Spread the word.<br />
        <span className="text-[#E65F2B]">Use the brand right.</span>
      </h1>
      <p className="text-base sm:text-lg text-white/60 mt-6 max-w-2xl">
        Everything you need to write, design, or talk about Christoreel — logos, colors, type, boilerplate copy, and a few rules of the road. Press, partners and creators welcome.
      </p>

      {/* Quick contact */}
      <div className="card-surface p-5 mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-testid="press-contact-card">
        <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0 bg-[#E65F2B]/15 border border-[#E65F2B]/30">
          <Mail className="h-5 w-5 text-[#E65F2B]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-[#E65F2B] mb-1">Press inquiries</p>
          <p className="font-medium">press@christoreel.com</p>
          <p className="text-xs text-white/50 mt-1">Response within 2 business days. For interviews, screening copies, or partnership questions.</p>
        </div>
        <button
          onClick={() => copy("email", "press@christoreel.com")}
          className="ghost-btn !py-2 !px-4 text-sm shrink-0"
          data-testid="press-copy-email"
        >
          {copied === "email" ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
          {copied === "email" ? "Copied" : "Copy"}
        </button>
      </div>

      {/* Logos */}
      <section className="mt-20" data-testid="press-logos-section">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
          <h2 className="font-display text-3xl font-black tracking-tighter">Logos</h2>
          <p className="text-xs text-white/40">PNG · 512px &amp; 1024px · all free for editorial use</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LOGOS.map((logo) => (
            <div key={logo.label} className="card-surface p-4 flex flex-col" data-testid={`logo-variant-${logo.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className={`aspect-square rounded-lg ${logo.bgClass} flex items-center justify-center p-6 mb-4`}>
                <img src={logo.preview} alt={logo.label} className="max-h-full max-w-full object-contain" />
              </div>
              <p className="font-display font-bold text-base">{logo.label}</p>
              <p className="text-xs text-white/50 mt-1 flex-1">{logo.description}</p>
              <div className="flex gap-2 mt-4 flex-wrap">
                {logo.downloads.map((d) => (
                  <a
                    key={d.href}
                    href={d.href}
                    download
                    className="ghost-btn !py-2 !px-3 text-xs"
                    data-testid={`logo-download-${d.size.replace(/\s+/g, "-").toLowerCase()}-${logo.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <Download className="h-3 w-3" />
                    {d.size}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Colors */}
      <section className="mt-20" data-testid="press-colors-section">
        <h2 className="font-display text-3xl font-black tracking-tighter mb-6">Colors</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PALETTE.map((c) => (
            <button
              key={c.hex}
              onClick={() => copy(c.hex, c.hex)}
              className="card-surface p-4 text-left hover:border-white/20 transition-colors group"
              data-testid={`color-swatch-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="h-20 rounded-md mb-3 ring-1 ring-white/10"
                style={{ backgroundColor: c.hex.startsWith("rgba") ? c.hex : c.hex }}
              />
              <div className="flex items-center justify-between gap-2">
                <p className="font-display font-bold text-sm">{c.name}</p>
                {copied === c.hex
                  ? <Check className="h-3.5 w-3.5 text-green-400" />
                  : <Copy className="h-3.5 w-3.5 text-white/30 group-hover:text-white/70 transition" />}
              </div>
              <p className="font-mono text-xs text-white/50 mt-1 break-all">{c.hex}</p>
              <p className="text-xs text-white/40 mt-2">{c.role}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Typography */}
      <section className="mt-20" data-testid="press-type-section">
        <h2 className="font-display text-3xl font-black tracking-tighter mb-6">Typography</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="card-surface p-6">
            <p className="text-eyebrow mb-2">Display · headlines</p>
            <p className="font-display text-5xl font-black tracking-tighter leading-none">Christo<span className="text-[#E65F2B]">reel</span></p>
            <p className="text-xs text-white/40 mt-4 font-mono">font-family: &quot;Archivo Black&quot;, &quot;Inter Tight&quot;, system-ui</p>
            <p className="text-xs text-white/50 mt-2">Bold, condensed, tight tracking. Used for hero copy, section titles, and the wordmark.</p>
          </div>
          <div className="card-surface p-6">
            <p className="text-eyebrow mb-2">Body · UI</p>
            <p className="text-2xl">Stories worth believing. Cinema worth sharing.</p>
            <p className="text-xs text-white/40 mt-4 font-mono">font-family: Inter, system-ui</p>
            <p className="text-xs text-white/50 mt-2">Inter for everything not a headline — paragraphs, buttons, navigation, captions.</p>
          </div>
        </div>
      </section>

      {/* Boilerplate */}
      <section className="mt-20" data-testid="press-boilerplate-section">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
          <h2 className="font-display text-3xl font-black tracking-tighter">Boilerplate</h2>
          <button
            onClick={() => copy("boilerplate", BOILERPLATE)}
            className="ghost-btn !py-2 !px-4 text-sm"
            data-testid="press-copy-boilerplate"
          >
            {copied === "boilerplate" ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
            {copied === "boilerplate" ? "Copied" : "Copy text"}
          </button>
        </div>
        <div className="card-surface p-6 sm:p-8 leading-relaxed text-white/80">
          <p className="text-eyebrow text-[#E65F2B] mb-3">About Christoreel · 60 words</p>
          <p>{BOILERPLATE}</p>
        </div>
        <div className="card-surface p-6 sm:p-8 leading-relaxed text-white/80 mt-3">
          <p className="text-eyebrow text-[#E65F2B] mb-3">One-liner</p>
          <p className="text-lg">A Naira-first streaming platform for African Christian cinema.</p>
        </div>
      </section>

      {/* Do / Don't */}
      <section className="mt-20" data-testid="press-rules-section">
        <h2 className="font-display text-3xl font-black tracking-tighter mb-6">Rules of the road</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card-surface p-6">
            <div className="flex items-center gap-2 mb-4">
              <Check className="h-4 w-4 text-green-400" />
              <p className="text-eyebrow text-green-400">Please do</p>
            </div>
            <ul className="space-y-2.5 text-sm text-white/80 list-disc ml-5 marker:text-green-400/60">
              <li>Keep at least <b>16px of clear space</b> on every side of the logo.</li>
              <li>Use the <b>Reverse</b> variant on photographic or dark backgrounds.</li>
              <li>Write the name as <b>Christoreel</b> — one word, capital C, lowercase rest.</li>
              <li>Refer to the orange <b>&quot;reel&quot;</b> styling when the brand name appears as text.</li>
              <li>Credit film stills to the channel/creator, not Christoreel.</li>
            </ul>
          </div>
          <div className="card-surface p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <p className="text-eyebrow text-red-400">Please don&apos;t</p>
            </div>
            <ul className="space-y-2.5 text-sm text-white/80 list-disc ml-5 marker:text-red-400/60">
              <li>Stretch, skew, or rotate the logo.</li>
              <li>Recolor the logo or change the dove/Africa/film-strip composition.</li>
              <li>Write the brand as &quot;ChristoReel&quot;, &quot;Christo Reel&quot;, or &quot;christoreel&quot;.</li>
              <li>Place the logo over busy imagery without the cream background.</li>
              <li>Use the logo to imply endorsement of unaffiliated content or events.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer credit */}
      <div className="text-center mt-20 pt-10 border-t border-white/5">
        <p className="text-xs text-white/40">
          Questions? Reach out to <a href="mailto:press@christoreel.com" className="text-white/70 underline-offset-2 hover:underline">press@christoreel.com</a> — we&apos;ll get back within 2 business days.
        </p>
      </div>
    </div>
  );
}
